import ConfirmModal from "@app/components/confirm-modal";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import AdvertiseService from "@app/services/http/advertise.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Meta } from "@app/types";
import { Advertise } from "@app/types/advertise.type";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const columnConfig: ColumnConfig[] = [
  {
    label: "Script",
    dataKey: "script",
    width: "20%",
    hasOrder: true,
  },
  {
    label: "Thời gian (s)",
    dataKey: "duration",
    width: "20%",
    hasOrder: true,
  },
  {
    label: "Book name",
    dataKey: "bookName",
    width: "20%",
    hasOrder: true,
  },
  {
    label: "Category name",
    dataKey: "categoryName",
    width: "20%",
    hasOrder: true,
  },
];

function AdvertiseManagement() {
  const [advertises, setAdvertises] = useState<Advertise[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  const navigate = useNavigate();

  useEffect(() => {
    AdvertiseService.getList(requestParams, abortController.signal).then(
      (res) => {
        setAdvertises(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateClick = () => {
    navigate(
      `/${RoutePath.ADVERTISE_MANAGEMENT}/${RoutePath.CREATE_ADVERTISE}`
    );
  };

  const handleEditClick = (advertise: Advertise) => {
    navigate(
      `/${RoutePath.ADVERTISE_MANAGEMENT}/${RoutePath.UPDATE_ADVERTISE}/${advertise.id}`
    );
  };

  const handleDeleteClick = (advertiseId: number) => {
    if (advertiseId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_ADVERTISE_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          AdvertiseService.delete(advertiseId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách Quảng cáo</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách Quảng cáo"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={advertises}
            hasCheckBox
            hasAction
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default AdvertiseManagement;
